import React, { useState, useMemo } from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  Row,
  Col,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import ReactDatetime from "components/CustomDatePicker";
import DollarInput from "components/DollarInput";
import Select from "components/Select";
import StatesInput from "components/StatesInput";
import { formatDateForPicker, getCurrentDateForAPI } from "utils/dateUtils";
import { getActiveColorStyle } from "utils/colorUtils";
import { getSelectStyles } from "utils/selectStyles";
import { formatPhoneNumber } from "utils/phoneUtils";
import { formatPriceWithDollar } from "utils/currency";
import { useGetCountries } from "api/countries/service";
import { useGetCustomerQR, useUpdateCustomerQR } from "api/customer-qrs/service";

const CustomerDetailQR = ({ activeColor = "secondary" }) => {
  const { id } = useParams();
  const { t } = useTranslation();
  const activeColorStyle = React.useMemo(
    () => getActiveColorStyle(activeColor),
    [activeColor]
  );
  const [isLightTheme, setIsLightTheme] = useState(false);
  const { data: countriesData } = useGetCountries();
  const { data: customerQRData, isLoading: isLoadingQR, isError: isErrorQR } = useGetCustomerQR(id);
  const updateCustomerQRMutation = useUpdateCustomerQR();

  React.useEffect(() => {
    const checkTheme = () =>
      setIsLightTheme(document.body.classList.contains("white-content"));
    checkTheme();
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, []);

  // API'den gelen customer verilerini form'a yükle
  React.useEffect(() => {
    if (customerQRData?.customer) {
      const customer = customerQRData.customer;
      
      setFormData((prev) => {
        const newData = { ...prev };
        
        // Text fields
        if (customer.ssn) newData.ssn = customer.ssn;
        if (customer.full_name) newData.full_name = customer.full_name;
        if (customer.cell_phone) newData.phone_number = customer.cell_phone;
        if (customer.how_did_hear) newData.howDidYouHear = customer.how_did_hear;
        if (customer.alt_id_type) newData.alt_id_type = customer.alt_id_type;
        
        // Date of Birth
        if (customer.date_of_birth) {
          newData.date_of_birth = customer.date_of_birth;
        }
        
        // Marital Status - lowercase çevir (UI'da "single", "married" kullanıyoruz)
        if (customer.marital_status) {
          newData.marital_status = customer.marital_status.toLowerCase();
        }
        
        // Current Address
        if (customer.current_address) {
          if (customer.current_address.address_1) {
            newData.address = customer.current_address.address_1;
          }
          // State'i dönüştür - state ID'yi number olarak sakla
          if (customer.current_address.state) {
            newData.state_you_live_in = {
              id: Number(customer.current_address.state),
              name: customer.current_address.state_name || '',
              abbreviation: customer.current_address.state_name || ''
            };
          }
        }
        
        // Current Employment
        if (customer.current_employment?.employment_type) {
          newData.current_employment = {
            employment_type: customer.current_employment.employment_type,
          };
        }
        
        return newData;
      });
    }
  }, [customerQRData]);

  const customSelectStyles = React.useMemo(() => {
    const baseStyles = getSelectStyles(activeColorStyle, isLightTheme);
    return {
      ...baseStyles,
      menu: (provided) => ({
        ...provided,
        backgroundColor: '#ffffff',
        borderRadius: 0,
        border: '1px solid rgba(29, 37, 59, 0.1)',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
      }),
      option: (provided, state) => ({
        ...baseStyles.option(provided, state),
        color: state.isSelected ? activeColorStyle.borderColor : '#1d253b',
      }),
      singleValue: (provided, state) => ({
        ...baseStyles.singleValue(provided, state),
        color: state.isDisabled ? 'rgba(29, 37, 59, 0.4)' : '#1d253b',
      }),
      input: (provided) => ({
        ...baseStyles.input(provided),
        color: '#1d253b',
      }),
      placeholder: (provided) => ({
        ...baseStyles.placeholder(provided),
        color: 'rgba(29, 37, 59, 0.6)',
      }),
      multiValueLabel: (provided) => ({
        ...baseStyles.multiValueLabel(provided),
        color: '#1d253b',
      }),
    };
  }, [activeColorStyle, isLightTheme]);

  const [formData, setFormData] = useState({
    date: getCurrentDateForAPI(),
    salesperson: "",
    howDidYouHear: "",
    ssn: "",
    full_name: "",
    date_of_birth: "",
    phone_number: "",
    marital_status: "",
    address: "",
    social_media_name: "",
    id_type: {
      dl: false,
      id: false,
      passport: false,
      ss_card: false,
      itin_yes: false,
      itin_no: false,
      itin: false,
    },
    employment_status: {
      self_employed: false,
      full_time: false,
      part_time: false,
    },
    income_proof: "",
    proof_of_residence: "",
    available_down_payment: "$0.00",
    car_interested: "",
    down_payment_method: "",
    state_you_live_in: null,
    notes: "",
    alt_id_type: "",
    alt_id_number: "",
    alt_id_country: "",
    alt_id_state: null,
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "phone_number") {
      const formatted = formatPhoneNumber(value);
      setFormData((prev) => ({ ...prev, [name]: formatted }));
    } else if (type === "checkbox") {
      // Checkbox için nested object handling
      const parts = name.split(".");
      if (parts.length === 2) {
        setFormData((prev) => ({
          ...prev,
          [parts[0]]: {
            ...prev[parts[0]],
            [parts[1]]: checked,
          },
        }));
      } else {
        setFormData((prev) => ({ ...prev, [name]: checked }));
      }
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleChange = (name, value) => {
    // Nested object için (örn: current_employment.employment_type)
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData((prev) => ({
        ...prev,
        [parent]: {
          ...(prev[parent] || {}),
          [child]: value,
        },
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  // Helper function to find country by id or name
  const findCountry = (value) => {
    if (!countriesData?.data) return undefined;
    return countriesData.data.find(
      (c) =>
        String(c.id) === String(value) ||
        c.name === value ||
        c.documentId === value
    );
  };

  // Alt ID country/state logic
  const selectedAltIdCountry = useMemo(
    () => findCountry(formData.alt_id_country),
    [countriesData, formData.alt_id_country]
  );
  const altIdStateOptions = selectedAltIdCountry?.states || [];
  const altIdHasStates =
    Array.isArray(altIdStateOptions) && altIdStateOptions.length > 0;

  // Current employment country/state logic
  const selectedEmploymentCountry = useMemo(
    () => findCountry(formData.current_employment?.employment_country),
    [countriesData, formData.current_employment?.employment_country]
  );
  const employmentStateOptions = selectedEmploymentCountry?.states || [];
  const employmentHasStates =
    Array.isArray(employmentStateOptions) && employmentStateOptions.length > 0;

  const maritalStatusOptions = [
    { value: "single", label: t("qr.maritalStatus.single") },
    { value: "married", label: t("qr.maritalStatus.married") },
    { value: "divorced", label: t("qr.maritalStatus.divorced") },
    { value: "widowed", label: t("qr.maritalStatus.widowed") },
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // PUT endpoint için doğru ID'yi kullan
    const qrId = customerQRData?.qr_id || customerQRData?.documentId || id;
    
    // API'nin beklediği formata dönüştür
    const payload = {};

    // Text fields
    if (formData.ssn) {
      payload.ssn = formData.ssn;
    }
    if (formData.full_name) {
      payload.full_name = formData.full_name;
    }
    if (formData.phone_number) {
      payload.cell_phone = formData.phone_number;
    }
    if (formData.howDidYouHear) {
      payload.how_did_hear = formData.howDidYouHear;
    }
    if (formData.alt_id_type) {
      payload.alt_id_type = formData.alt_id_type;
    }

    // Date of Birth - ISO string'e çevir
    if (formData.date_of_birth) {
      const dateValue = typeof formData.date_of_birth === 'string' 
        ? new Date(formData.date_of_birth) 
        : formData.date_of_birth;
      
      if (dateValue instanceof Date && !isNaN(dateValue)) {
        payload.date_of_birth = dateValue.toISOString();
      }
    }

    // Marital Status - Capitalize first letter (API "Single", "Married" bekliyor)
    if (formData.marital_status) {
      payload.marital_status = formData.marital_status.charAt(0).toUpperCase() + formData.marital_status.slice(1);
    }

    // Current Address - JSON object
    if (formData.address || formData.state_you_live_in) {
      payload.current_address = {};
      if (formData.address) {
        payload.current_address.address_1 = formData.address;
      }
      if (formData.state_you_live_in?.id) {
        payload.current_address.state = String(formData.state_you_live_in.id);
        payload.current_address.state_name = formData.state_you_live_in.abbreviation || formData.state_you_live_in.name;
      }
    }

    // Current Employment - JSON object
    if (formData.current_employment?.employment_type) {
      payload.current_employment = {
        employment_type: formData.current_employment.employment_type,
      };
    }

    // Mutation ile gönder
    updateCustomerQRMutation.mutate({ id: qrId, data: payload });
  };

  // QR durumu kontrolü
  const qrStatus = customerQRData?.data?.qr_status ?? customerQRData?.qr_status;
  const isExpDate = customerQRData?.data?.is_exp_date ?? customerQRData?.is_exp_date;
  const isQRCompleted = qrStatus === false;
  const isSessionExpired = qrStatus === true && isExpDate === false;

  if (isLoadingQR) {
    return (
      <div className="content" style={{ backgroundColor: 'white', color: '#6b6b6b', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <p style={{ fontSize: '18px', color: '#6b6b6b' }}>{t("common.loading")}</p>
        </div>
      </div>
    );
  }

  if (isSessionExpired) {
    return (
      <div className="content" style={{ backgroundColor: 'white', color: '#6b6b6b', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Row className="justify-content-center" style={{ width: '100%' }}>
          <Col md="10" lg="9" xl="8">
            <Card style={{ backgroundColor: 'white', color: '#6b6b6b', position: 'relative', overflow: 'hidden' }}>
              {/* Logo Background - Soluk ve Ortada */}
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '800px',
                height: '440px',
                backgroundImage: 'url(/logo.svg)',
                backgroundSize: 'contain',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
                opacity: 0.025,
                pointerEvents: 'none',
                zIndex: 0
              }} />
              <CardHeader style={{ 
                color: '#6b6b6b',
                position: 'relative',
                zIndex: 1
              }}>
                <CardTitle tag="h1" style={{ color: 'Black', fontSize: '30px', fontWeight: 'bold', textAlign: 'center' }}>{t("qr.title")}</CardTitle>
              </CardHeader>
              <CardBody style={{ color: '#6b6b6b', position: 'relative', zIndex: 1, textAlign: 'center', padding: '60px 20px' }}>
                <h3 style={{ color: '#d32f2f', fontSize: '24px', fontWeight: 'bold', marginBottom: '20px' }}>
                  {t("qr.sessionExpired")}
                </h3>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    );
  }

  if (isQRCompleted) {
    return (
      <div className="content" style={{ backgroundColor: 'white', color: '#6b6b6b', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Row className="justify-content-center" style={{ width: '100%' }}>
          <Col md="10" lg="9" xl="8">
            <Card style={{ backgroundColor: 'white', color: '#6b6b6b', position: 'relative', overflow: 'hidden' }}>
              {/* Logo Background - Soluk ve Ortada */}
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '800px',
                height: '440px',
                backgroundImage: 'url(/logo.svg)',
                backgroundSize: 'contain',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
                opacity: 0.025,
                pointerEvents: 'none',
                zIndex: 0
              }} />
              <CardHeader style={{ 
                color: '#6b6b6b',
                position: 'relative',
                zIndex: 1
              }}>
                <CardTitle tag="h1" style={{ color: 'Black', fontSize: '30px', fontWeight: 'bold', textAlign: 'center' }}>{t("qr.title")}</CardTitle>
              </CardHeader>
              <CardBody style={{ color: '#6b6b6b', position: 'relative', zIndex: 1, textAlign: 'center', padding: '60px 20px' }}>
                <h3 style={{ color: '#1d253b', fontSize: '24px', fontWeight: 'bold', marginBottom: '20px' }}>
                  {t("qr.processCompleted")}
                </h3>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    );
  }

  return (
    <div className="content" style={{ backgroundColor: 'white', color: '#6b6b6b' }}>
      
      <Row className="justify-content-center" style={{ backgroundColor: 'lightgrey' }}>
      
        <Col md="10" lg="9" xl="8" style={{ }}>
        
          <Card style={{ backgroundColor: 'white', color: '#6b6b6b', position: 'relative', overflow: 'hidden' }}>
            {/* Logo Background - Soluk ve Ortada */}
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '800px',
              height: '440px',
              backgroundImage: 'url(/logo.svg)',
              backgroundSize: 'contain',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
              opacity: 0.025,
              pointerEvents: 'none',
              zIndex: 0
            }} />
            <CardHeader style={{ 
              color: '#6b6b6b',
              position: 'relative',
              zIndex: 1
            }}>
              <CardTitle tag="h1" style={{ color: 'Black',fontSize: '30px',fontWeight: 'bold', textAlign: 'center' }}>{t("qr.title")}</CardTitle>
            </CardHeader>
            <CardBody style={{ color: '#6b6b6b', position: 'relative', zIndex: 1 }}>
              <Form onSubmit={handleSubmit} style={{ color: '#6b6b6b' }}>
                {/* Top Section */}
                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label style={{ color: '#6b6b6b' }}>{t("qr.date")}</Label>
                      <ReactDatetime
                        inputProps={{
                          className: "form-control",
                          placeholder: t("qr.selectDate"),
                          style: { borderRadius: 0, color: '#6b6b6b' },
                        }}
                        value={
                          formData.date
                            ? formatDateForPicker(formData.date)
                            : formData.date
                        }
                        onChange={(date) =>
                          setFormData((prev) => ({ ...prev, date }))
                        }
                        dateFormat="MM-DD-YYYY"
                        timeFormat={false}
                        closeOnSelect={true}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label style={{ color: '#6b6b6b' }}>{t("qr.salesperson")}</Label>
                      <Input
                        name="salesperson"
                        type="text"
                        value={formData.salesperson}
                        onChange={handleInputChange}
                        style={{ color: '#6b6b6b' }}
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label style={{ color: '#6b6b6b' }}>{t("qr.howDidYouHear")}</Label>
                      <Input
                        name="howDidYouHear"
                        type="text"
                        value={formData.howDidYouHear}
                        onChange={handleInputChange}
                        style={{ color: '#6b6b6b' }}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label style={{ color: '#6b6b6b' }}>{t("customerDetailTab.ssn")}</Label>
                      <Input
                        id="ssn"
                        name="ssn"
                        type="text"
                        value={formData.ssn}
                        onChange={handleInputChange}
                        className="form-control"
                        placeholder={t("customerDetailTab.enterSSN")}
                        style={{ borderRadius: 0, fontSize: "14px", color: '#6b6b6b' }}
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label style={{ color: '#6b6b6b' }}>{t("qr.fullName")}</Label>
                                            <Input
                        name="full_name"
                        type="text"
                        value={formData.full_name}
                        onChange={handleInputChange}
                        disabled={!!customerQRData?.customer?.full_name}
                        style={{ 
                          color: '#6b6b6b',
                          backgroundColor: customerQRData?.customer?.full_name ? '#ffffff' : undefined,
                          opacity: customerQRData?.customer?.full_name ? 1 : undefined
                        }}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label style={{ color: '#6b6b6b' }}>{t("customerDetailTab.dateOfBirth")}</Label>
                      <ReactDatetime
                        inputProps={{
                          className: "form-control",
                          placeholder: t("customerDetailTab.selectDate"),
                          style: { borderRadius: 0, color: '#6b6b6b' },
                        }}
                        value={
                          formData.date_of_birth
                            ? formatDateForPicker(formData.date_of_birth)
                            : formData.date_of_birth
                        }
                        onChange={(date) =>
                          setFormData((prev) => ({
                            ...prev,
                            date_of_birth: date,
                          }))
                        }
                        dateFormat="MM-DD-YYYY"
                        timeFormat={false}
                        closeOnSelect={true}
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label style={{ color: '#6b6b6b' }}>{t("qr.phoneNumber")}</Label>
                      <Input
                        name="phone_number"
                        type="text"
                        value={formData.phone_number}
                        onChange={handleInputChange}
                        placeholder="(XXX) XXX-XXXX"
                        style={{ color: '#6b6b6b' }}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label style={{ color: '#6b6b6b' }}>{t("qr.maritalStatusLabel")}</Label>
                      <Select
                        styles={customSelectStyles}
                        className="react-select"
                        placeholder={t("common.select")}
                        isSearchable
                        closeMenuOnSelect={true}
                        value={
                          formData.marital_status
                            ? maritalStatusOptions.find(
                                (opt) => opt.value === formData.marital_status
                              )
                            : null
                        }
                        onChange={(option) =>
                          setFormData((prev) => ({
                            ...prev,
                            marital_status: option?.value || "",
                          }))
                        }
                        options={maritalStatusOptions}
                        isClearable
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col md="12">
                    <FormGroup>
                      <Label style={{ color: '#6b6b6b' }}>{t("qr.address")}</Label>
                      <Input
                        name="address"
                        type="text"
                        value={formData.address}
                        onChange={handleInputChange}
                        style={{ color: '#6b6b6b' }}
                      />
                    </FormGroup>
                  </Col>
                </Row>

                {/* Middle Section */}
                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label style={{ color: '#6b6b6b' }}>{t("qr.applicantSocialMediaName")}</Label>
                      <Input
                        name="social_media_name"
                        type="text"
                        value={formData.social_media_name}
                        onChange={handleInputChange}
                        style={{ color: '#6b6b6b' }}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label for="alt_id_type" style={{ color: '#6b6b6b' }}>
                        {t("customerDetailTab.alternateGovtIdType")}
                      </Label>
                      <Select
                        activeColor={activeColor}
                        styles={customSelectStyles}
                        className="react-select"
                        placeholder={t("customerDetailTab.selectIdType")}
                        name="alt_id_type"
                        isSearchable={false}
                        closeMenuOnSelect={true}
                        value={
                          formData.alt_id_type
                            ? {
                                value: formData.alt_id_type,
                                label:
                                  formData.alt_id_type === "1"
                                    ? t("customerDetailTab.usPassport")
                                    : formData.alt_id_type === "2"
                                    ? t("customerDetailTab.usStateId")
                                    : formData.alt_id_type === "3"
                                    ? t("customerDetailTab.usMilitaryId")
                                    : formData.alt_id_type === "4"
                                    ? t("customerDetailTab.usDhsId")
                                    : formData.alt_id_type === "5"
                                    ? t("customerDetailTab.usCisId")
                                    : formData.alt_id_type === "6"
                                    ? t("customerDetailTab.usStateDeptId")
                                    : formData.alt_id_type === "7"
                                    ? t("customerDetailTab.photoId")
                                    : formData.alt_id_type === "8"
                                    ? t("customerDetailTab.matriculaConsular")
                                    : formData.alt_id_type === "9"
                                    ? t("customerDetailTab.foreignPassport")
                                    : formData.alt_id_type === "10"
                                    ? t("customerDetailTab.natoId")
                                    : formData.alt_id_type === "11"
                                    ? t(
                                        "customerDetailTab.otherMilitaryStatus"
                                      )
                                    : formData.alt_id_type,
                              }
                            : null
                        }
                        onChange={(option) => {
                          handleChange("alt_id_type", option?.value || "");
                        }}
                        options={[
                          {
                            value: "1",
                            label: t("customerDetailTab.usPassport"),
                          },
                          {
                            value: "2",
                            label: t("customerDetailTab.usStateId"),
                          },
                          {
                            value: "3",
                            label: t("customerDetailTab.usMilitaryId"),
                          },
                          {
                            value: "4",
                            label: t("customerDetailTab.usDhsId"),
                          },
                          {
                            value: "5",
                            label: t("customerDetailTab.usCisId"),
                          },
                          {
                            value: "6",
                            label: t("customerDetailTab.usStateDeptId"),
                          },
                          {
                            value: "7",
                            label: t("customerDetailTab.photoId"),
                          },
                          {
                            value: "8",
                            label: t("customerDetailTab.matriculaConsular"),
                          },
                          {
                            value: "9",
                            label: t("customerDetailTab.foreignPassport"),
                          },
                          {
                            value: "10",
                            label: t("customerDetailTab.natoId"),
                          },
                          {
                            value: "11",
                            label: t("customerDetailTab.otherMilitaryStatus"),
                          },
                        ]}
                        isClearable
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label for="employment_type" style={{ color: '#6b6b6b' }}>
                        {t("customerDetailTab.employmentType")}
                      </Label>
                      <Select
                        activeColor={activeColor}
                        styles={customSelectStyles}
                        className="react-select"
                        placeholder={t("customerDetailTab.selectType")}
                        name="employment_type"
                        isSearchable={false}
                        closeMenuOnSelect={true}
                        value={
                          formData.current_employment?.employment_type
                            ? {
                                value:
                                  formData.current_employment.employment_type,
                                label:
                                  formData.current_employment
                                    .employment_type === "Full-Time"
                                    ? t("customerDetailTab.fullTime")
                                    : formData.current_employment
                                        .employment_type === "Part-Time"
                                    ? t("customerDetailTab.partTime")
                                    : formData.current_employment
                                        .employment_type === "Self-Employed"
                                    ? t("customerDetailTab.selfEmployed")
                                    : formData.current_employment
                                        .employment_type === "Contract"
                                    ? t("customerDetailTab.contract")
                                    : formData.current_employment
                                        .employment_type === "Unemployed"
                                    ? t("customerDetailTab.unemployed")
                                    : formData.current_employment
                                        .employment_type,
                              }
                            : null
                        }
                        onChange={(option) => {
                          handleChange(
                            "current_employment.employment_type",
                            option?.value || ""
                          );
                        }}
                        options={[
                          {
                            value: "Full-Time",
                            label: t("customerDetailTab.fullTime"),
                          },
                          {
                            value: "Part-Time",
                            label: t("customerDetailTab.partTime"),
                          },
                          {
                            value: "Self-Employed",
                            label: t("customerDetailTab.selfEmployed"),
                          },
                          {
                            value: "Contract",
                            label: t("customerDetailTab.contract"),
                          },
                          {
                            value: "Unemployed",
                            label: t("customerDetailTab.unemployed"),
                          },
                        ]}
                        isClearable
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label style={{ color: '#6b6b6b' }}>{t("qr.incomeProof")}</Label>
                      <Select
                        activeColor={activeColor}
                        styles={customSelectStyles}
                        className="react-select"
                        placeholder={t("common.select")}
                        isSearchable={false}
                        closeMenuOnSelect={true}
                        value={
                          formData.income_proof
                            ? {
                                value: formData.income_proof,
                                label:
                                  formData.income_proof === "bank_statements"
                                    ? t("qr.bankStatements")
                                    : formData.income_proof === "cash_app"
                                    ? t("qr.cashApp")
                                    : formData.income_proof === "chime"
                                    ? t("qr.chime")
                                    : formData.income_proof === "pay_stubs"
                                    ? t("qr.payStubs")
                                    : formData.income_proof,
                              }
                            : null
                        }
                        onChange={(option) =>
                          handleChange("income_proof", option?.value || "")
                        }
                        options={[
                          {
                            value: "bank_statements",
                            label: t("qr.bankStatements"),
                          },
                          {
                            value: "cash_app",
                            label: t("qr.cashApp"),
                          },
                          {
                            value: "chime",
                            label: t("qr.chime"),
                          },
                          {
                            value: "pay_stubs",
                            label: t("qr.payStubs"),
                          },
                        ]}
                        isClearable
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label style={{ color: '#6b6b6b' }}>{t("qr.proofOfResidenceMail")}</Label>
                      <Select
                        activeColor={activeColor}
                        styles={customSelectStyles}
                        className="react-select"
                        placeholder={t("common.select")}
                        isSearchable={false}
                        closeMenuOnSelect={true}
                        value={
                          formData.proof_of_residence
                            ? {
                                value: formData.proof_of_residence,
                                label:
                                  formData.proof_of_residence === "bill"
                                    ? t("qr.bill")
                                    : formData.proof_of_residence ===
                                      "bank_statement"
                                    ? t("qr.bankStatement")
                                    : formData.proof_of_residence ===
                                      "lease_agreement"
                                    ? t("qr.leaseAgreement")
                                    : formData.proof_of_residence,
                              }
                            : null
                        }
                        onChange={(option) =>
                          handleChange(
                            "proof_of_residence",
                            option?.value || ""
                          )
                        }
                        options={[
                          {
                            value: "bill",
                            label: t("qr.bill"),
                          },
                          {
                            value: "bank_statement",
                            label: t("qr.bankStatement"),
                          },
                          {
                            value: "lease_agreement",
                            label: t("qr.leaseAgreement"),
                          },
                        ]}
                        isClearable
                      />
                    </FormGroup>
                  </Col>
                </Row>

                {/* Bottom Section */}
                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label style={{ color: '#6b6b6b' }}>{t("qr.availableDownPayment")}</Label>
                      <DollarInput
                        name="available_down_payment"
                        value={formData.available_down_payment}
                        onChange={(value) =>
                          setFormData((prev) => ({
                            ...prev,
                            available_down_payment: value,
                          }))
                        }
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label style={{ color: '#6b6b6b' }}>{t("qr.howWillYouPayYourDown")}</Label>
                      <Select
                        activeColor={activeColor}
                        styles={customSelectStyles}
                        className="react-select"
                        placeholder={t("common.select")}
                        isSearchable={false}
                        closeMenuOnSelect={true}
                        value={
                          formData.down_payment_method
                            ? {
                                value: formData.down_payment_method,
                                label:
                                  formData.down_payment_method === "cash"
                                    ? t("qr.cash")
                                    : formData.down_payment_method === "card"
                                    ? t("qr.card")
                                    : formData.down_payment_method === "zelle"
                                    ? t("qr.zelle")
                                    : formData.down_payment_method === "cashapp"
                                    ? t("qr.cashapp")
                                    : formData.down_payment_method,
                              }
                            : null
                        }
                        onChange={(option) =>
                          handleChange(
                            "down_payment_method",
                            option?.value || ""
                          )
                        }
                        options={[
                          {
                            value: "cash",
                            label: t("qr.cash"),
                          },
                          {
                            value: "card",
                            label: t("qr.card"),
                          },
                          {
                            value: "zelle",
                            label: t("qr.zelle"),
                          },
                          {
                            value: "cashapp",
                            label: t("qr.cashapp"),
                          },
                        ]}
                        isClearable
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label style={{ color: '#6b6b6b' }}>{t("qr.carInterestedIn")}</Label>
                      <Input
                        name="car_interested"
                        type="text"
                        value={formData.car_interested}
                        onChange={handleInputChange}
                        style={{ color: '#6b6b6b' }}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label style={{ color: '#6b6b6b' }}>{t("qr.stateYouLiveIn")}</Label>
                      <StatesInput
                        name="state_you_live_in"
                        value={formData.state_you_live_in}
                        onChange={(stateObject) => {
                          setFormData((prev) => ({
                            ...prev,
                            state_you_live_in: stateObject
                              ? { 
                                  id: stateObject.id, 
                                  name: stateObject.name,
                                  abbreviation: stateObject.abbreviation 
                                }
                              : null,
                          }));
                        }}
                        styles={customSelectStyles}
                        placeholder={t("common.select")}
                        isSearchable
                        isClearable
                        activeColor={activeColor}
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col md="12">
                    <FormGroup>
                      <Label style={{ color: '#6b6b6b' }}>{t("qr.notes")}</Label>
                      <Input
                        name="notes"
                        type="textarea"
                        rows="3"
                        value={formData.notes}
                        onChange={handleInputChange}
                        style={{ color: '#6b6b6b' }}
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col md="12" className="text-right mt-3">
                    <Button 
                      color="primary" 
                      type="submit" 
                      size="lg"
                      disabled={updateCustomerQRMutation.isPending}
                    >
                      {updateCustomerQRMutation.isPending ? t("common.loading") : t("qr.submit")}
                    </Button>
                  </Col>
                </Row>
              </Form>
            </CardBody>
          </Card>
        </Col>
      </Row>
      
    </div>
  );
};

export default CustomerDetailQR;
